/** Plans d’abonnement, CRM, budget — types front partagés. */

/** Essai gratuit à l’inscription (jours). */
export const TRIAL_DAYS = 14;

/** Inactivité au-delà de laquelle le compte est purgé (mois). */
export const INACTIVITY_PURGE_MONTHS = 12;

/** Délai entre échéances d’abonnement fractionné (jours). */
export const INSTALLMENT_INTERVAL_DAYS = 28;

/** Seuil d’alerte avant fin d’essai ou d’abonnement (jours). */
export const SUBSCRIPTION_EXPIRY_ALERT_DAYS = 7;

/** Devise des abonnements (franc CFA UEMOA). */
export const SUBSCRIPTION_CURRENCY = 'XOF';

export const SUBSCRIPTION_PLANS = [
  {
    key: 'monthly',
    label: 'Mensuel',
    labelEn: 'Monthly',
    price: 23_000,
    currency: SUBSCRIPTION_CURRENCY,
    durationDays: 30,
    installmentCount: 1,
    installmentAmounts: [23_000],
    description: 'Payable en une seule fois. Accès complet pendant 30 jours.',
    descriptionEn: 'Paid in one installment. Full access for 30 days.',
  },
  {
    key: 'semestrial',
    label: 'Semestriel',
    labelEn: 'Semi-annual',
    price: 105_000,
    currency: SUBSCRIPTION_CURRENCY,
    durationDays: 182,
    installmentCount: 2,
    installmentAmounts: [52_500, 52_500],
    description: 'Deux échéances de 52 500 F CFA (initial + 28 jours).',
    descriptionEn: 'Two installments of 52,500 F CFA (initial + after 28 days).',
  },
  {
    key: 'annual',
    label: 'Annuel',
    labelEn: 'Annual',
    price: 164_000,
    currency: SUBSCRIPTION_CURRENCY,
    durationDays: 365,
    installmentCount: 3,
    /** 54 667 + 54 667 + 54 666 = 164 000 F CFA (trois modalités après 28 j). */
    installmentAmounts: [54_667, 54_667, 54_666],
    description: 'Trois échéances (54 667 F CFA puis 54 667 F CFA et 54 666 F CFA après 28 jours).',
    descriptionEn: 'Three installments (54,667 F CFA then 54,667 F CFA and 54,666 F CFA after 28 days).',
  },
] as const;

export type SubscriptionPlanKey = (typeof SUBSCRIPTION_PLANS)[number]['key'];

export function getSubscriptionPlan(key: string) {
  return SUBSCRIPTION_PLANS.find((p) => p.key === key) ?? null;
}

export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  active: 'Actif',
  pending_payment: 'Paiement en attente',
  cancelled: 'Annulé',
  expired: 'Expiré',
  failed: 'Échoué',
};

export function subscriptionStatusLabel(status: string): string {
  return SUBSCRIPTION_STATUS_LABELS[status] ?? status;
}

/**
 * Un abonnement n’est réellement souscrit qu’après au moins un paiement confirmé.
 * `pending_payment` ne doit jamais ouvrir les fonctionnalités ni apparaître comme plan actuel.
 */
export function isPaidSubscriptionRecord(sub: {
  status?: string | null;
  plan_type?: string | null;
  installments_paid?: number | null;
}): boolean {
  if (!sub || sub.status !== 'active') return false;
  if (sub.plan_type === 'free') return false;
  return Number(sub.installments_paid ?? 0) >= 1;
}

export const MARKETPLACE_INQUIRY_STATUS_LABELS: Record<string, string> = {
  nouveau: 'Nouvelle',
  en_cours: 'En cours',
  traite: 'Traitée',
  archive: 'Archivée',
  new: 'Nouvelle',
  in_progress: 'En cours',
  closed: 'Clôturée',
  open: 'Ouverte',
  pending: 'En attente',
};

export function marketplaceInquiryStatusLabel(status: string): string {
  return MARKETPLACE_INQUIRY_STATUS_LABELS[status] ?? status;
}

export const PREOCCUPATION_STATUS_LABELS: Record<string, string> = {
  open: 'Ouverte',
  in_progress: 'En cours',
  resolved: 'Clôturée',
  archived: 'Archivée',
  closed: 'Fermée',
  sent: 'Envoyée',
};

export function preoccupationStatusLabel(status: string): string {
  return PREOCCUPATION_STATUS_LABELS[status] ?? status;
}

export function formatSubscriptionPrice(
  amount: number,
  locale: 'fr' | 'en' = 'fr',
  currency: string = SUBSCRIPTION_CURRENCY,
) {
  const code = (currency || SUBSCRIPTION_CURRENCY).toUpperCase();
  if (code === 'EUR') {
    return new Intl.NumberFormat(locale === 'en' ? 'en-EU' : 'fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(amount);
  }
  const formatted = new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'fr-FR', {
    maximumFractionDigits: 0,
  }).format(amount);
  return `${formatted} F CFA`;
}

/** @deprecated Les tarifs d’abonnement sont en F CFA — utiliser formatSubscriptionPrice. */
export function formatEuro(amount: number, locale: 'fr' | 'en' = 'fr') {
  return formatSubscriptionPrice(amount, locale);
}

/** Statuts stockés en base (crm_leads.status). */
export const CRM_STATUSES = [
  { key: 'nouveau', label: 'Nouveau' },
  { key: 'contacte', label: 'Contacté' },
  { key: 'negociation', label: 'Négociation' },
  { key: 'gagne', label: 'Gagné' },
  { key: 'perdu', label: 'Perdu' },
] as const;

export type CrmStatusKey = (typeof CRM_STATUSES)[number]['key'];

/**
 * Pipeline de conversion en mode processus (réf. CRM Pro).
 * L’étape « Résultats » regroupe gagné + perdu.
 */
export const CRM_PIPELINE_STAGES = [
  {
    key: 'nouveau',
    step: 1,
    label: 'Nouveau',
    description: 'nouvelle expression de besoin',
    tone: 'grey',
    statuses: ['nouveau'] as const,
  },
  {
    key: 'contacte',
    step: 2,
    label: 'Contacté',
    description: 'relation établie avec le prospect',
    tone: 'blue',
    statuses: ['contacte'] as const,
  },
  {
    key: 'negociation',
    step: 3,
    label: 'Négociation',
    description: 'discussion pour accord',
    tone: 'purple',
    statuses: ['negociation'] as const,
  },
  {
    key: 'resultats',
    step: 4,
    label: 'Résultats',
    description: 'gagné / perdu',
    tone: 'green',
    statuses: ['gagne', 'perdu'] as const,
  },
] as const;

/** Normalise d’anciens statuts éventuels vers le pipeline actuel. */
export function normalizeCrmStatus(status: string): CrmStatusKey {
  if (status === 'discussion') return 'contacte';
  if (status === 'proposition') return 'negociation';
  if ((CRM_STATUSES as readonly { key: string }[]).some((s) => s.key === status)) {
    return status as CrmStatusKey;
  }
  return 'nouveau';
}

export const BUDGET_CATEGORIES = [
  'Investissement',
  'Marketing',
  'Opérations',
  'RH',
  'Juridique',
  'Technologie',
  'Autres',
] as const;

export type SwotItem = {
  element: string;
  proposition_ia: string;
  recommandation: string;
};

export type SwotData = {
  strengths: SwotItem[];
  weaknesses: SwotItem[];
  opportunities: SwotItem[];
  threats: SwotItem[];
};

/** Piliers CDC — Matrice stratégique */
export const STRATEGIC_PILLARS = [
  { code: 'orientation', pilier: 'Orientation stratégique', ordre: 1 },
  { code: 'offre', pilier: 'Offre de valeur', ordre: 2 },
  { code: 'commercial', pilier: 'Développement commercial', ordre: 3 },
  { code: 'operations', pilier: 'Organisation et opérations', ordre: 4 },
  { code: 'finance', pilier: 'Performance financière', ordre: 5 },
  { code: 'innovation', pilier: 'Innovation et développement', ordre: 6 },
] as const;

export type StrategicMatrixRow = {
  code: string;
  pilier: string;
  ordre: number;
  diagnostic: string;
  intention: string;
  objectifs_smart: string;
  actions_prioritaires: string;
  indicateurs_kpi: string;
};

/** @deprecated Ancien format — préférer StrategicMatrixRow */
export type MatrixAxis = {
  code: string;
  titre: string;
  ordre: number;
  objectifs_strategiques: string[];
  resultats_attendus: string[];
  indicateurs_cles_kpi: string[];
  responsables: string[];
};

export function emptySwot(): SwotData {
  return { strengths: [], weaknesses: [], opportunities: [], threats: [] };
}

export function emptySwotItem(): SwotItem {
  return { element: '', proposition_ia: '', recommandation: '' };
}

export function emptyStrategicMatrix(): StrategicMatrixRow[] {
  return STRATEGIC_PILLARS.map((p) => ({
    code: p.code,
    pilier: p.pilier,
    ordre: p.ordre,
    diagnostic: '',
    intention: '',
    objectifs_smart: '',
    actions_prioritaires: '',
    indicateurs_kpi: '',
  }));
}

export function normalizeStrategicMatrix(raw: unknown): StrategicMatrixRow[] {
  const base = emptyStrategicMatrix();
  if (!Array.isArray(raw) || raw.length === 0) return base;
  const first = raw[0] as Record<string, unknown>;
  // Ancien format (axes avec objectifs_strategiques[])
  if (first && 'objectifs_strategiques' in first && !('diagnostic' in first)) {
    return base;
  }
  return base.map((row) => {
    const found = (raw as StrategicMatrixRow[]).find(
      (r) => r.code === row.code || r.pilier === row.pilier || r.ordre === row.ordre,
    );
    if (!found) return row;
    return {
      ...row,
      diagnostic: found.diagnostic ?? '',
      intention: found.intention ?? '',
      objectifs_smart: found.objectifs_smart ?? '',
      actions_prioritaires: found.actions_prioritaires ?? '',
      indicateurs_kpi: found.indicateurs_kpi ?? '',
    };
  });
}
